import { Typography } from '@/components/atoms';
import { MapPinnedIcon } from '@/components/atoms/icons';
import { PlaceholderImage } from '@/components/atoms/PlaceholderImage';
import { ImageCarousel } from '@/components/molecules/ImageCarousel';
import { useUIColors } from '@/core/hooks';
import { trackGuestProjectCarouselManualChange } from '@/core/services/trackDynatraceCatalog';
import { useI18nContext } from '@/core/providers/I18nProvider';
import React, { useCallback } from 'react';
import {
  Pressable,
  StyleSheet,
  View,
  type ImageSourcePropType,
} from 'react-native';
import type {
  GuestDetailsLayout,
  GuestDetailsTypeScale,
} from '../constants/guestLayout';
import type { GuestLabels } from '../constants/getGuestLabels';
import type { GuestUnitDetailsModel } from '../types/guest.types';
import { GuestUnitAboutAccordion } from './GuestUnitAboutAccordion';
import { GuestUnitDocumentsAccordion } from './GuestUnitDocumentsAccordion';

export interface GuestUnitDetailsMainColumnProps {
  readonly unit: GuestUnitDetailsModel;
  readonly unitImages: ReadonlyArray<ImageSourcePropType>;
  readonly layout: GuestDetailsLayout;
  readonly typeScale: GuestDetailsTypeScale;
  readonly labels: GuestLabels;
  readonly onMapPress: () => void;
  readonly onDocumentPress: (documentId: string) => void;
  readonly downloadingDocumentId?: string | null;
}

function GuestUnitHero({
  unit,
  unitImages,
  layout,
}: Readonly<{
  unit: GuestUnitDetailsModel;
  unitImages: ReadonlyArray<ImageSourcePropType>;
  layout: GuestDetailsLayout;
}>) {
  const colors = useUIColors();
  const { isRTL } = useI18nContext();
  const badgeSide = isRTL ? 'right' : 'left';
  const handleManualCarouselChange = useCallback(
    (imageIndex: number) => {
      trackGuestProjectCarouselManualChange(unit.id, imageIndex);
    },
    [unit.id],
  );

  return (
    <View
      style={[
        styles.hero,
        unitImages.length === 0 && {
          height: layout.carouselHeight,
          backgroundColor: colors.backgroundLevel2,
        },
      ]}
    >
      {unitImages.length > 0 ? (
        <ImageCarousel
          images={[...unitImages]}
          height={layout.carouselHeight}
          showNavigationButtons={!layout.isPhone}
          onManualIndexChange={handleManualCarouselChange}
        />
      ) : (
        <View
          style={[
            styles.heroPlaceholder,
            { backgroundColor: colors.backgroundLevel2 },
          ]}
        >
          <PlaceholderImage />
        </View>
      )}
      {unit.statusBadge ? (
        <View
          style={[
            styles.constructionBadge,
            layout.isPhone && styles.constructionBadgeMobile,
            {
              [badgeSide]: layout.isPhone ? 12 : 16,
            },
          ]}
          pointerEvents="none"
        >
          <Typography
            variant="caption"
            color="primary"
            style={[
              styles.constructionBadgeText,
              layout.isPhone && styles.constructionBadgeTextMobile,
            ]}
          >
            {unit.statusBadge}
          </Typography>
        </View>
      ) : null}
    </View>
  );
}

function GuestUnitHeader({
  unit,
  typeScale,
  onMapPress,
}: Readonly<{
  unit: GuestUnitDetailsModel;
  typeScale: GuestDetailsTypeScale;
  onMapPress: () => void;
}>) {
  const colors = useUIColors();

  return (
    <View style={styles.unitHeader}>
      <View style={styles.titleRow}>
        <Typography
          variant={typeScale.pageTitle}
          color="primary"
          style={styles.titleText}
        >
          {unit.title}
        </Typography>
        {unit.latitude != null && unit.longitude != null ? (
          <Pressable
            onPress={onMapPress}
            accessibilityRole="button"
            accessibilityLabel={unit.location || 'Open map'}
          >
            <MapPinnedIcon
              size={typeScale.mapIconSize}
              color={colors.fontPrimary}
            />
          </Pressable>
        ) : null}
      </View>
      <View style={styles.locationDescription}>
        {unit.location ? (
          <Typography
            variant={typeScale.location}
            color="secondary"
            style={[
              styles.locationText,
              { lineHeight: typeScale.locationLineHeight },
            ]}
          >
            {unit.location}
          </Typography>
        ) : null}
        {unit.description?.trim() ? (
          <Typography
            variant={typeScale.description}
            color="secondary"
            style={[
              styles.descriptionText,
              { lineHeight: typeScale.descriptionLineHeight },
            ]}
          >
            {unit.description.trim()}
          </Typography>
        ) : null}
      </View>
    </View>
  );
}

/**
 * Main details column: hero, header, About + Documents (each self-hides when empty).
 */
export function GuestUnitDetailsMainColumn({
  unit,
  unitImages,
  layout,
  typeScale,
  labels,
  onMapPress,
  onDocumentPress,
  downloadingDocumentId = null,
}: Readonly<GuestUnitDetailsMainColumnProps>) {
  return (
    <View style={styles.main}>
      <GuestUnitHero unit={unit} unitImages={unitImages} layout={layout} />
      <GuestUnitHeader
        unit={unit}
        typeScale={typeScale}
        onMapPress={onMapPress}
      />
      <GuestUnitAboutAccordion
        unit={unit}
        layout={layout}
        typeScale={typeScale}
        labels={labels}
      />
      <GuestUnitDocumentsAccordion
        documents={unit.documents ?? []}
        layout={layout}
        typeScale={typeScale}
        labels={labels}
        onDocumentPress={onDocumentPress}
        downloadingDocumentId={downloadingDocumentId}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  main: {
    gap: 16,
    minWidth: 0,
    maxWidth: '100%',
    width: '100%',
  },
  hero: {
    position: 'relative',
    width: '100%',
    maxWidth: '100%',
    borderRadius: 16,
    overflow: 'hidden',
  },
  heroPlaceholder: {
    width: '100%',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
  },
  constructionBadge: {
    position: 'absolute',
    top: 16,
    maxWidth: '85%',
    backgroundColor: '#E7E7E7',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 8,
    zIndex: 3,
  },
  constructionBadgeMobile: {
    top: 12,
    paddingHorizontal: 6,
  },
  constructionBadgeText: {
    fontFamily: 'Matter-SemiBold',
    lineHeight: 16,
    letterSpacing: 0,
  },
  constructionBadgeTextMobile: {
    lineHeight: 22,
  },
  unitHeader: {
    gap: 8,
    width: '100%',
    minWidth: 0,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    width: '100%',
  },
  titleText: {
    letterSpacing: 0,
    flexShrink: 1,
  },
  locationDescription: {
    gap: 16,
    width: '100%',
    minWidth: 0,
  },
  locationText: {
    lineHeight: 18,
    letterSpacing: 0,
  },
  descriptionText: {
    lineHeight: 24,
    letterSpacing: 0,
  },
});

export default GuestUnitDetailsMainColumn;
